<template>
  <div class="page-content">
    <breadcrumb-index
      :items="breadcrumbs"
      icon="bell"
      :badge="recentNotifications.length ? `${recentNotifications.length} recent` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <span v-if="!isSuperAdmin" class="approval-hint">
          <vue-feather type="info" size="14" />
          Alerts require super admin approval
        </span>
      </template>
    </breadcrumb-index>

    <div class="alerts-layout">
      <!-- Send Alert Form -->
      <div class="alert-form-card">
        <div class="card-header-custom">
          <vue-feather type="send" size="18" />
          <h5>Send Alert / Push Notification</h5>
        </div>

        <form @submit.prevent="sendNotification" class="alert-form">
          <div class="form-group">
            <label class="form-label">Title <span class="text-danger">*</span></label>
            <input v-model="form.title" type="text" class="form-control" placeholder="e.g., New Test Available" required maxlength="50" />
            <small class="text-muted">{{ form.title.length }}/50 characters</small>
          </div>

          <div class="form-group">
            <label class="form-label">Message <span class="text-danger">*</span></label>
            <textarea v-model="form.message" class="form-control" rows="3" placeholder="Notification message..." required maxlength="200"></textarea>
            <small class="text-muted">{{ form.message.length }}/200 characters</small>
          </div>

          <!-- Target Audience -->
          <div class="form-group">
            <label class="form-label">Target Audience <span class="text-danger">*</span></label>
            <div class="target-options">
              <label
                v-for="opt in targetOptions"
                :key="opt.value"
                :class="['target-option', { selected: form.target === opt.value }]"
              >
                <input type="radio" v-model="form.target" :value="opt.value" class="d-none" />
                <vue-feather :type="opt.icon" size="16" />
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- User Search (when target is 'specific_users') -->
          <div v-if="form.target === 'specific_users'" class="form-group">
            <label class="form-label">Select Users <span class="text-danger">*</span></label>
            <div class="user-search-box">
              <div class="search-input-wrapper">
                <vue-feather type="search" size="16" />
                <input
                  v-model="userSearchQuery"
                  type="text"
                  class="user-search-input"
                  placeholder="Search by name, email, or mobile..."
                  @input="debouncedSearchUsers"
                  @focus="showUserDropdown = true"
                />
                <span v-if="searchingUsers" class="search-spinner">
                  <vue-feather type="loader" size="14" class="spin" />
                </span>
              </div>

              <!-- User Dropdown -->
              <div v-if="showUserDropdown && userSearchResults.length" class="user-dropdown">
                <div
                  v-for="u in userSearchResults"
                  :key="u.id"
                  class="user-dropdown-item"
                  :class="{ disabled: isUserSelected(u.id) }"
                  @click="selectUser(u)"
                >
                  <div class="user-avatar-sm">{{ getInitial(u.fullName || u.email) }}</div>
                  <div class="user-dropdown-info">
                    <span class="user-dropdown-name">{{ u.fullName || 'Unknown' }}</span>
                    <span class="user-dropdown-email">{{ u.email }}</span>
                  </div>
                  <vue-feather v-if="isUserSelected(u.id)" type="check" size="14" class="text-success" />
                </div>
              </div>
              <div v-if="showUserDropdown && userSearchQuery && !searchingUsers && !userSearchResults.length" class="user-dropdown">
                <div class="user-dropdown-empty">No users found</div>
              </div>
            </div>

            <!-- Selected Users Tags -->
            <div v-if="form.selectedUsers.length" class="selected-users">
              <div v-for="u in form.selectedUsers" :key="u.id" class="user-tag">
                <span>{{ u.fullName || u.email }}</span>
                <button type="button" @click="removeUser(u.id)" class="user-tag-remove">
                  <vue-feather type="x" size="12" />
                </button>
              </div>
              <small class="text-muted">{{ form.selectedUsers.length }} user(s) selected</small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Schedule <span class="optional-tag">(Optional)</span></label>
              <input v-model="form.scheduledAt" type="datetime-local" class="form-control" />
            </div>
            <div class="form-group">
              <label class="form-label">Deep Link <span class="optional-tag">(Optional)</span></label>
              <input v-model="form.deepLink" type="text" class="form-control" placeholder="e.g., /exam/123" />
            </div>
          </div>

          <!-- Approval Notice -->
          <div v-if="!isSuperAdmin" class="approval-notice">
            <vue-feather type="shield" size="16" />
            <div>
              <strong>Approval Required</strong>
              <p>This alert will be sent to the super admin for approval before being delivered to users.</p>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn btn-primary" :disabled="loading || !isFormValid">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              <vue-feather v-else :type="isSuperAdmin ? 'send' : 'check-square'" size="14" class="me-1" />
              {{ isSuperAdmin ? 'Send Notification' : 'Submit for Approval' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Right Panel -->
      <div class="alert-side-panel">
        <!-- Pending Approvals (for super_admin) -->
        <div v-if="isSuperAdmin && pendingAlerts.length" class="side-card pending-card">
          <div class="side-card-header">
            <vue-feather type="clock" size="16" />
            <h6>Pending Alert Approvals</h6>
            <span class="count-badge">{{ pendingAlerts.length }}</span>
          </div>
          <div class="pending-list">
            <div v-for="alert in pendingAlerts" :key="alert.id" class="pending-item">
              <div class="pending-item-header">
                <strong>{{ alert.title }}</strong>
                <span :class="['target-badge', `target-${alert.target}`]">{{ formatTarget(alert.target) }}</span>
              </div>
              <p class="pending-message">{{ alert.message }}</p>
              <div class="pending-meta">
                <span class="submitter">
                  <vue-feather type="user" size="12" />
                  {{ alert.submittedByName || 'Admin' }}
                </span>
                <span class="pending-date">{{ formatDate(alert.submittedAt) }}</span>
              </div>
              <div v-if="alert.selectedUsers && alert.selectedUsers.length" class="pending-users">
                <vue-feather type="users" size="12" />
                <span>{{ alert.selectedUsers.length }} specific user(s)</span>
              </div>
              <div class="pending-actions">
                <button class="btn btn-sm btn-success" @click="approveAlert(alert)" :disabled="approvingId === alert.id">
                  <vue-feather type="check" size="13" class="me-1" />
                  Approve & Send
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="rejectAlert(alert)" :disabled="approvingId === alert.id">
                  <vue-feather type="x" size="13" class="me-1" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Notifications -->
        <div class="side-card">
          <div class="side-card-header">
            <vue-feather type="bell" size="16" />
            <h6>Recent Notifications</h6>
          </div>
          <div v-if="recentNotifications.length" class="recent-list">
            <div v-for="notification in recentNotifications" :key="notification.id" class="recent-item">
              <div class="recent-item-header">
                <strong>{{ notification.title }}</strong>
                <span :class="['status-dot', notification.status === 'delivered' ? 'dot-success' : notification.status === 'pending_approval' ? 'dot-warning' : notification.status === 'rejected' ? 'dot-danger' : 'dot-info']"></span>
              </div>
              <p class="recent-message">{{ notification.message }}</p>
              <div class="recent-meta">
                <span class="recent-date">{{ formatDate(notification.sentAt || notification.createdAt) }}</span>
                <span v-if="notification.delivered" class="badge bg-info-subtle text-info">{{ notification.delivered }} delivered</span>
                <span v-if="notification.status === 'pending_approval'" class="badge bg-warning-subtle text-warning">Pending Approval</span>
                <span v-if="notification.status === 'rejected'" class="badge bg-danger-subtle text-danger">Rejected</span>
              </div>
            </div>
          </div>
          <div v-else class="empty-recent">
            <vue-feather type="bell-off" size="32" />
            <p>No notifications sent yet</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Click outside handler for user dropdown -->
    <div v-if="showUserDropdown" class="dropdown-overlay" @click="showUserDropdown = false"></div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'NotificationsPage',
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction, promptTextarea } = useNotifications()

    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Alerts', active: true }
    ]

    const targetOptions = [
      { value: 'all', label: 'All Users', icon: 'globe' },
      { value: 'active', label: 'Active (7d)', icon: 'activity' },
      { value: 'inactive', label: 'Inactive', icon: 'user-minus' },
      { value: 'premium', label: 'Premium', icon: 'award' },
      { value: 'specific_users', label: 'Specific Users', icon: 'user-check' }
    ]

    const form = reactive({
      title: '',
      message: '',
      target: 'all',
      scheduledAt: '',
      deepLink: '',
      selectedUsers: []
    })

    const loading = ref(false)
    const recentNotifications = ref([])
    const pendingAlerts = ref([])
    const approvingId = ref(null)

    // User search state
    const userSearchQuery = ref('')
    const userSearchResults = ref([])
    const searchingUsers = ref(false)
    const showUserDropdown = ref(false)
    let searchTimeout = null

    const isFormValid = computed(() => {
      if (!form.title || !form.message) return false
      if (form.target === 'specific_users' && form.selectedUsers.length === 0) return false
      return true
    })

    const isUserSelected = (id) => form.selectedUsers.some(u => u.id === id)

    const getInitial = (name) => (name || 'U').charAt(0).toUpperCase()

    const selectUser = (user) => {
      if (isUserSelected(user.id)) return
      form.selectedUsers.push({ id: user.id, fullName: user.fullName, email: user.email })
      userSearchQuery.value = ''
      userSearchResults.value = []
      showUserDropdown.value = false
    }

    const removeUser = (id) => {
      form.selectedUsers = form.selectedUsers.filter(u => u.id !== id)
    }

    const searchUsers = async () => {
      const q = userSearchQuery.value.trim()
      if (q.length < 2) {
        userSearchResults.value = []
        return
      }
      searchingUsers.value = true
      try {
        const res = await api.get('/v1/admin/app-users', {
          params: { search: q, searchType: 'all', limit: 10 }
        })
        userSearchResults.value = res.data?.users || res.data?.data || []
        showUserDropdown.value = true
      } catch (err) {
        console.error('User search failed:', err)
        userSearchResults.value = []
      } finally {
        searchingUsers.value = false
      }
    }

    const debouncedSearchUsers = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(searchUsers, 300)
    }

    const resetForm = () => {
      form.title = ''
      form.message = ''
      form.target = 'all'
      form.scheduledAt = ''
      form.deepLink = ''
      form.selectedUsers = []
    }

    const fetchRecentNotifications = async () => {
      try {
        const response = await api.get('/v1/admin/notifications/recent')
        recentNotifications.value = response.data?.notifications || (Array.isArray(response.data) ? response.data : [])
      } catch (err) {
        console.error('Failed to fetch notifications:', err)
      }
    }

    const fetchPendingAlerts = async () => {
      if (!isSuperAdmin.value) return
      try {
        const response = await api.get('/v1/admin/approvals', {
          params: { status: 'pending_review', contentType: 'notification' }
        })
        const data = response.data
        const approvals = Array.isArray(data) ? data : (data?.approvals || data?.data || [])
        pendingAlerts.value = approvals.map(a => {
          const snapshot = parseSnapshot(a.contentSnapshot)
          return {
            id: a.id,
            title: snapshot?.title || 'Untitled',
            message: snapshot?.message || '',
            target: snapshot?.target || 'all',
            selectedUsers: snapshot?.selectedUsers || [],
            scheduledAt: snapshot?.scheduledAt || null,
            deepLink: snapshot?.deepLink || '',
            submittedBy: a.submittedBy,
            submittedByName: a.submittedByName || null,
            submittedAt: a.submittedAt || a.createdAt
          }
        })
        // Try to resolve submitter names
        if (pendingAlerts.value.some(a => !a.submittedByName)) {
          try {
            const usersRes = await api.get('/v1/admin/admin-users')
            const users = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.users || [])
            const userMap = {}
            users.forEach(u => { userMap[u.id] = u.fullName || u.email })
            pendingAlerts.value.forEach(a => {
              if (!a.submittedByName && userMap[a.submittedBy]) {
                a.submittedByName = userMap[a.submittedBy]
              }
            })
          } catch (e) { /* ignore */ }
        }
      } catch (err) {
        console.error('Failed to fetch pending alerts:', err)
      }
    }

    const parseSnapshot = (snapshot) => {
      if (!snapshot) return null
      if (typeof snapshot === 'object') return snapshot
      try { return JSON.parse(snapshot) } catch { return null }
    }

    const sendNotification = async () => {
      if (!isFormValid.value) return
      loading.value = true
      try {
        const payload = {
          title: form.title,
          message: form.message,
          target: form.target,
          scheduledAt: form.scheduledAt || undefined,
          deepLink: form.deepLink || undefined
        }

        if (form.target === 'specific_users') {
          payload.userIds = form.selectedUsers.map(u => u.id)
        }

        if (isSuperAdmin.value) {
          // Super admin sends directly
          await api.post('/v1/admin/notifications/send', payload)
          toast.success('Notification sent successfully')
        } else {
          // Non-super admin submits for approval
          const approvalPayload = {
            contentType: 'notification',
            contentSnapshot: JSON.stringify({
              title: form.title,
              message: form.message,
              target: form.target,
              selectedUsers: form.target === 'specific_users' ? form.selectedUsers : [],
              userIds: form.target === 'specific_users' ? form.selectedUsers.map(u => u.id) : [],
              scheduledAt: form.scheduledAt || null,
              deepLink: form.deepLink || null
            }),
            submittedBy: authStore.user?.id
          }
          await api.post('/v1/admin/approvals', approvalPayload)
          toast.success('Alert submitted for super admin approval')
        }

        resetForm()
        fetchRecentNotifications()
      } catch (err) {
        toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to send notification')
      } finally {
        loading.value = false
      }
    }

    const approveAlert = async (alert) => {
      approvingId.value = alert.id
      try {
        // First approve the approval record
        await api.put(`/v1/admin/approvals/${alert.id}`, {
          id: alert.id,
          action: 'approve',
          reviewedBy: authStore.user?.id,
          notes: 'Approved and sent'
        })

        // Then send the actual notification
        const payload = {
          title: alert.title,
          message: alert.message,
          target: alert.target,
          scheduledAt: alert.scheduledAt || undefined,
          deepLink: alert.deepLink || undefined
        }
        if (alert.target === 'specific_users' && alert.selectedUsers?.length) {
          payload.userIds = alert.selectedUsers.map(u => u.id)
        }
        await api.post('/v1/admin/notifications/send', payload)

        pendingAlerts.value = pendingAlerts.value.filter(a => a.id !== alert.id)
        toast.success('Alert approved and sent to users')
        fetchRecentNotifications()
      } catch (err) {
        toast.error(err.response?.data?.error?.message || 'Failed to approve alert')
      } finally {
        approvingId.value = null
      }
    }

    const rejectAlert = async (alert) => {
      const notes = await promptTextarea('Reject this alert?', {
        placeholder: 'Reason for rejection (optional)...',
        confirmButtonText: 'Reject'
      })
      if (notes === null) return

      approvingId.value = alert.id
      try {
        await api.put(`/v1/admin/approvals/${alert.id}`, {
          id: alert.id,
          action: 'reject',
          reviewedBy: authStore.user?.id,
          notes: notes || 'Rejected'
        })
        pendingAlerts.value = pendingAlerts.value.filter(a => a.id !== alert.id)
        toast.info('Alert rejected')
      } catch (err) {
        toast.error('Failed to reject alert')
      } finally {
        approvingId.value = null
      }
    }

    const formatTarget = (target) => {
      const labels = {
        all: 'All Users',
        active: 'Active',
        inactive: 'Inactive',
        premium: 'Premium',
        specific_users: 'Specific Users'
      }
      return labels[target] || target
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      })
    }

    onMounted(() => {
      fetchRecentNotifications()
      fetchPendingAlerts()
    })

    onUnmounted(() => {
      clearTimeout(searchTimeout)
    })

    return {
      breadcrumbs, isSuperAdmin, targetOptions,
      form, loading, isFormValid,
      recentNotifications, pendingAlerts, approvingId,
      userSearchQuery, userSearchResults, searchingUsers, showUserDropdown,
      isUserSelected, getInitial, selectUser, removeUser, debouncedSearchUsers,
      sendNotification, approveAlert, rejectAlert,
      formatTarget, formatDate
    }
  }
}
</script>

<style scoped>
.alerts-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 1.5rem;
  align-items: start;
}

@media (max-width: 1024px) {
  .alerts-layout {
    grid-template-columns: 1fr;
  }
}

/* Approval Hint */
.approval-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #f59e0b;
  background: #fef3c7;
  padding: 4px 10px;
  border-radius: 6px;
}

:global(body.dark-mode) .approval-hint {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

/* Form Card */
.alert-form-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

:global(body.dark-mode) .alert-form-card {
  background: #1e293b;
  border-color: #334155;
}

.card-header-custom {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #1e293b;
}

:global(body.dark-mode) .card-header-custom {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

.card-header-custom h5 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.alert-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.optional-tag {
  font-weight: 400;
  color: #94a3b8;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

/* Target Options */
.target-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.target-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.target-option:hover {
  border-color: #93c5fd;
  color: #3b82f6;
}

.target-option.selected {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #2563eb;
}

:global(body.dark-mode) .target-option {
  background: #0f172a;
  border-color: #334155;
  color: #94a3b8;
}

:global(body.dark-mode) .target-option:hover {
  border-color: #60a5fa;
  color: #60a5fa;
}

:global(body.dark-mode) .target-option.selected {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

/* User Search */
.user-search-box {
  position: relative;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  transition: border-color 0.2s;
}

.search-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

:global(body.dark-mode) .search-input-wrapper {
  background: #1e293b;
  border-color: #374151;
}

.search-input-wrapper svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.user-search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 0;
  font-size: 0.875rem;
  background: transparent;
}

:global(body.dark-mode) .user-search-input {
  color: #f3f4f6;
}

.search-spinner {
  color: #3b82f6;
}

.user-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
  margin-top: 4px;
}

:global(body.dark-mode) .user-dropdown {
  background: #1e293b;
  border-color: #334155;
}

.user-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.user-dropdown-item:hover:not(.disabled) {
  background: #f1f5f9;
}

.user-dropdown-item.disabled {
  opacity: 0.5;
  cursor: default;
}

:global(body.dark-mode) .user-dropdown-item:hover:not(.disabled) {
  background: #334155;
}

.user-avatar-sm {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.user-dropdown-info {
  flex: 1;
  min-width: 0;
}

.user-dropdown-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

:global(body.dark-mode) .user-dropdown-name {
  color: #f1f5f9;
}

.user-dropdown-email {
  display: block;
  font-size: 11px;
  color: #94a3b8;
}

.user-dropdown-empty {
  padding: 16px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
}

/* Selected Users Tags */
.selected-users {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.user-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px 4px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  font-size: 12px;
  color: #1e40af;
}

:global(body.dark-mode) .user-tag {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #93c5fd;
}

.user-tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.15s;
}

.user-tag-remove:hover {
  background: #dbeafe;
  color: #ef4444;
}

/* Approval Notice */
.approval-notice {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fbbf24;
  border-radius: 10px;
  color: #92400e;
}

.approval-notice p {
  margin: 2px 0 0;
  font-size: 12px;
  opacity: 0.85;
}

.approval-notice strong {
  font-size: 13px;
}

:global(body.dark-mode) .approval-notice {
  background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
  border-color: #b45309;
  color: #fef3c7;
}

.form-actions {
  padding-top: 0.5rem;
}

/* Side Panel */
.side-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

:global(body.dark-mode) .side-card {
  background: #1e293b;
  border-color: #334155;
}

.side-card + .side-card {
  margin-top: 1rem;
}

.side-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
}

:global(body.dark-mode) .side-card-header {
  background: #0f172a;
  border-color: #334155;
  color: #94a3b8;
}

.side-card-header h6 {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  flex: 1;
}

.count-badge {
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Pending Alerts */
.pending-card {
  border-color: #fbbf24;
}

.pending-card .side-card-header {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

:global(body.dark-mode) .pending-card {
  border-color: #b45309;
}

:global(body.dark-mode) .pending-card .side-card-header {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.pending-list {
  padding: 0.5rem;
}

.pending-item {
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid #f3f4f6;
  transition: background 0.15s;
}

.pending-item + .pending-item {
  margin-top: 0.5rem;
}

.pending-item:hover {
  background: #f9fafb;
}

:global(body.dark-mode) .pending-item {
  border-color: #334155;
}

:global(body.dark-mode) .pending-item:hover {
  background: #0f172a;
}

.pending-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.pending-item-header strong {
  font-size: 13px;
  color: #1e293b;
}

:global(body.dark-mode) .pending-item-header strong {
  color: #f1f5f9;
}

.target-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}

.target-all { background: #dbeafe; color: #1e40af; }
.target-active { background: #dcfce7; color: #166534; }
.target-inactive { background: #fee2e2; color: #991b1b; }
.target-premium { background: #fef3c7; color: #92400e; }
.target-specific_users { background: #ede9fe; color: #5b21b6; }

:global(body.dark-mode) .target-all { background: rgba(59, 130, 246, 0.15); color: #93c5fd; }
:global(body.dark-mode) .target-active { background: rgba(34, 197, 94, 0.15); color: #86efac; }
:global(body.dark-mode) .target-inactive { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
:global(body.dark-mode) .target-premium { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
:global(body.dark-mode) .target-specific_users { background: rgba(139, 92, 246, 0.15); color: #c4b5fd; }

.pending-message {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 6px;
  line-height: 1.4;
}

:global(body.dark-mode) .pending-message {
  color: #94a3b8;
}

.pending-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.submitter {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pending-users {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #8b5cf6;
  margin-bottom: 8px;
}

.pending-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.pending-actions .btn {
  font-size: 12px;
  display: flex;
  align-items: center;
}

/* Recent Notifications */
.recent-list {
  padding: 0.5rem;
}

.recent-item {
  padding: 0.75rem;
  border-radius: 8px;
  transition: background 0.15s;
}

.recent-item + .recent-item {
  border-top: 1px solid #f3f4f6;
}

:global(body.dark-mode) .recent-item + .recent-item {
  border-top-color: #334155;
}

.recent-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.recent-item-header strong {
  font-size: 13px;
  color: #1e293b;
}

:global(body.dark-mode) .recent-item-header strong {
  color: #f1f5f9;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-success { background: #22c55e; }
.dot-warning { background: #f59e0b; }
.dot-danger { background: #ef4444; }
.dot-info { background: #3b82f6; }

.recent-message {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 6px;
  line-height: 1.4;
}

:global(body.dark-mode) .recent-message {
  color: #94a3b8;
}

.recent-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.recent-date {
  color: #94a3b8;
}

.empty-recent {
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
}

.empty-recent p {
  margin: 8px 0 0;
  font-size: 13px;
}

/* Spin animation */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
